import firebase from '../config/firebase.config';

const db = firebase.firestore();

export const APP_VERSION_CONFIG_COLLECTION = 'appVersionConfig';

/** Desktop app installer download links live on these simple, non-arch-specific docs. */
export const DOWNLOAD_PLATFORMS = ['windows', 'macos', 'linux'];

const EMPTY_PLATFORM_CONFIG = { downloadUrl: null, currentVersion: null };

/**
 * Reads `downloadUrl`/`currentVersion` off the three platform docs.
 * @param {firebase.firestore.DocumentSnapshot[]} snapshotsInOrder
 * @returns {Record<'windows'|'macos'|'linux', { downloadUrl: string|null, currentVersion: string|null }>}
 */
export function mapDownloadUrlSnapshots(snapshotsInOrder) {
  return DOWNLOAD_PLATFORMS.reduce((acc, platform, index) => {
    const snap = snapshotsInOrder[index];
    const data = snap && snap.exists ? snap.data() : null;
    acc[platform] = {
      downloadUrl: (data && data.downloadUrl) || null,
      currentVersion: (data && data.currentVersion) || null,
    };
    return acc;
  }, {});
}

// Cached for the session — refetching on every mount would add a Firestore
// read per page visit for data that only changes when staff publish a new
// link. All callers share one promise; parallel mounts get the same in-flight
// request. Mirrors src/services/applicationInfo.js's caching approach.
let _downloadUrlsPromise = null;

/**
 * Loads the desktop app's per-platform installer download URL + version
 * directly from Firestore (`appVersionConfig/{windows,macos,linux}`) — the
 * single source of truth also used by the "App Versions" admin page and
 * (server-side) partner provisioning.
 * @returns {Promise<Record<'windows'|'macos'|'linux', { downloadUrl: string|null, currentVersion: string|null }>>}
 */
export function fetchDesktopAppDownloadUrls() {
  if (_downloadUrlsPromise) return _downloadUrlsPromise;
  _downloadUrlsPromise = (async () => {
    try {
      const col = db.collection(APP_VERSION_CONFIG_COLLECTION);
      const snapshots = await Promise.all(
        DOWNLOAD_PLATFORMS.map((platform) => col.doc(platform).get())
      );
      return mapDownloadUrlSnapshots(snapshots);
    } catch (error) {
      _downloadUrlsPromise = null; // allow retry on next call
      console.error('Error fetching desktop app download URLs:', error);
      return {
        windows: EMPTY_PLATFORM_CONFIG,
        macos: EMPTY_PLATFORM_CONFIG,
        linux: EMPTY_PLATFORM_CONFIG,
      };
    }
  })();
  return _downloadUrlsPromise;
}
